/**
 * apps/web/src/components/nav-items.ts
 *
 * Canonical navigation model shared by the desktop sidebar and the mobile
 * bottom bar + "Altro" drawer (docs/07-ux/navigation-and-screens.md §2-3).
 *
 * `primary: true` marks the 4 sections that appear directly in the mobile
 * bottom bar; the rest live behind the "Altro" drawer.
 *
 * Labels are localized, so the nav model is produced by the `useNavItems` hook
 * (it reads the active dictionary) rather than a module-level constant.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Home,
  Building2,
  FolderKanban,
  Banknote,
  FileText,
  MessageSquare,
  Users,
  BarChart3,
  Library,
  Table2,
  ShieldCheck,
  Workflow,
  Settings,
} from 'lucide-react';
import { useStrings } from '../i18n';

export interface NavItem {
  readonly key: string;
  readonly label: string;
  readonly to: string;
  readonly icon: LucideIcon;
  /** Shown directly in the mobile bottom bar (vs the "Altro" drawer). */
  readonly primary: boolean;
}

/** The full nav model in display order, with localized labels. */
export function useNavItems(): readonly NavItem[] {
  const strings = useStrings();
  return [
    { key: 'oggi', label: strings.nav.oggi, to: '/oggi', icon: Home, primary: true },
    { key: 'clienti', label: strings.nav.clienti, to: '/clienti', icon: Building2, primary: true },
    { key: 'progetti', label: strings.nav.progetti, to: '/progetti', icon: FolderKanban, primary: true },
    { key: 'soldi', label: strings.nav.soldi, to: '/soldi', icon: Banknote, primary: true },
    { key: 'documenti', label: strings.nav.documenti, to: '/documenti', icon: FileText, primary: false },
    { key: 'messaggi', label: strings.nav.messaggi, to: '/messaggi', icon: MessageSquare, primary: false },
    { key: 'team', label: strings.nav.team, to: '/team', icon: Users, primary: false },
    { key: 'report', label: strings.nav.report, to: '/report', icon: BarChart3, primary: false },
    { key: 'libreria', label: strings.nav.libreria, to: '/libreria', icon: Library, primary: false },
    { key: 'tabella', label: strings.nav.tabella, to: '/tabella', icon: Table2, primary: false },
    { key: 'governance', label: strings.nav.governance, to: '/governance', icon: ShieldCheck, primary: false },
    { key: 'automazioni', label: strings.nav.automazioni, to: '/automazioni', icon: Workflow, primary: false },
    { key: 'impostazioni', label: strings.nav.impostazioni, to: '/impostazioni', icon: Settings, primary: false },
  ];
}

/** Just the primary sections (mobile bottom bar). */
export function usePrimaryNavItems(): readonly NavItem[] {
  return useNavItems().filter((item) => item.primary);
}
